import React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Heart, Flower2, Sparkles, Download, RotateCcw, Quote, DoorOpen } from "lucide-react";
import { FloatingHearts } from "./FloatingHearts";

const SectionRibbon = ({ index, icon: Icon, title }) => (
  <div className="flex items-center gap-4 mb-8">
    <div className="relative flex items-center justify-center w-11 h-11 rounded-full border border-primary/40 text-primary shrink-0">
      <Icon className="w-5 h-5" />
      <motion.span
        className="absolute -top-1 -right-1"
        animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5] }}
        transition={{ duration: 2.4, repeat: Infinity }}
      >
        <Heart className="w-3 h-3 text-[#ff5a6e]" fill="#ff5a6e" />
      </motion.span>
    </div>
    <div>
      <p className="text-[11px] uppercase tracking-[0.3em] text-primary/70">{`0${index}`}</p>
      <h2 className="font-serif-display text-3xl sm:text-4xl rose-gold-text leading-tight">{title}</h2>
    </div>
  </div>
);

const Block = ({ label, children }) => (
  <div className="mb-5">
    <p className="text-sm uppercase tracking-[0.18em] text-primary/80 mb-1.5">{label}</p>
    <p className="text-[15px] sm:text-base text-foreground/90 reading-prose">{children}</p>
  </div>
);

export const ResultView = ({ t, lang, reading, drawn, form, onNew, onDownload }) => {
  const nameOf = (c) => (lang === "de" ? c.name_de : c.name_en);
  const chakraLabels = [
    ["heart", t.chakra_heart],
    ["solar_plexus", t.chakra_solar],
    ["third_eye", t.chakra_third],
    ["root", t.chakra_root],
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="max-w-4xl mx-auto px-5 sm:px-8 py-14"
    >
      <div className="text-center mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-primary/70 mb-3">{t.result_for}</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl rose-gold-text glow-rose">
          {form.name} <span className="text-accent">{t.and}</span> {form.partner_name}
        </h1>
        {reading.intro && (
          <p className="mt-6 max-w-2xl mx-auto text-foreground/80 italic font-serif-display text-lg sm:text-xl leading-relaxed">
            <Quote className="inline w-4 h-4 text-primary/60 mr-1 -mt-2" />
            {reading.intro}
          </p>
        )}
      </div>

      {/* Section 1: Cards */}
      <section className="mb-16" data-testid="result-section-cards">
        <SectionRibbon index={1} icon={Sparkles} title={t.section1} />
        <div className="space-y-8">
          {(reading.cards || []).map((rc, i) => {
            const card = drawn[i];
            return (
              <div key={i} className="grid sm:grid-cols-[180px_1fr] gap-5 sm:gap-7 velvet-rose rounded-2xl p-5 sm:p-6">
                {card && (
                  <div className="mx-auto sm:mx-0 w-[150px] sm:w-full">
                    <div className="rounded-xl overflow-hidden border border-primary/40 card-glow" style={{ aspectRatio: "0.67" }}>
                      <img src={card.img} alt={nameOf(card)} className="w-full h-full object-cover" />
                    </div>
                    <p className="text-center mt-2 text-sm text-muted-foreground">{rc.position}</p>
                  </div>
                )}
                <div>
                  <h3 className="font-serif-display text-2xl text-primary mb-4">{rc.name || (card && nameOf(card))}</h3>
                  <Block label={t.classic}>{rc.classic}</Block>
                  <Block label={t.soulmate}>{rc.soulmate}</Block>
                  <Block label={t.partner_feelings}>{rc.partner_feelings}</Block>
                  <Block label={t.development}>{rc.development}</Block>
                  <Block label={t.blockage}>{rc.blockage}</Block>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Section 2: Love */}
      {reading.love && (
        <section className="mb-16" data-testid="result-section-love">
          <SectionRibbon index={2} icon={Heart} title={t.section2} />
          <div className="grid sm:grid-cols-2 gap-5">
            {[
              [t.love_feels, reading.love.feels],
              [t.love_blocks, reading.love.blocks],
              [t.love_chances, reading.love.chances],
              [t.love_understand, reading.love.understand],
            ].map(([label, text], i) => (
              <div key={i} className="velvet-rose rounded-2xl p-6">
                <h3 className="font-serif-display text-xl text-primary mb-3">{label}</h3>
                <p className="text-[15px] text-foreground/90 reading-prose">{text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Section 3: Chakras */}
      {reading.chakras && (
        <section className="mb-16" data-testid="result-section-chakras">
          <SectionRibbon index={3} icon={Flower2} title={t.section3} />
          <div className="space-y-4">
            {chakraLabels.map(([key, label]) => {
              const c = reading.chakras[key];
              if (!c) return null;
              return (
                <div key={key} className="velvet-rose rounded-2xl p-6">
                  <h3 className="font-serif-display text-2xl text-accent mb-4">{label}</h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Block label={t.chakra_blockage}>{c.blockage}</Block>
                    <Block label={t.chakra_influence}>{c.influence}</Block>
                    <Block label={t.chakra_healing}>{c.healing}</Block>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Section 4: Summary */}
      {reading.summary && (
        <section className="mb-14" data-testid="result-section-summary">
          <SectionRibbon index={4} icon={Sparkles} title={t.section4} />
          <div className="relative overflow-hidden velvet-rose rounded-2xl p-7 sm:p-9 card-glow space-y-6">
            <FloatingHearts count={8} area="bottom" />
            <div className="relative z-10 space-y-6">
              <Block label={t.sum_message}>{reading.summary.message}</Block>
              <Block label={t.sum_core}>{reading.summary.core}</Block>
              <div className="grid sm:grid-cols-2 gap-x-8">
                <Block label={t.sum_release}>{reading.summary.release}</Block>
                <Block label={t.sum_invite}>{reading.summary.invite}</Block>
              </div>
              <Block label={t.sum_next}>{reading.summary.next_step}</Block>
            </div>
          </div>
        </section>
      )}

      {/* Open gate finale */}
      <div className="text-center mb-12">
        <DoorOpen className="w-10 h-10 mx-auto text-primary mb-3" />
        <p className="font-serif-display text-xl sm:text-2xl rose-gold-text glow-rose max-w-lg mx-auto">
          {lang === "de"
            ? "Das Tor bleibt geöffnet – deine Seelenreise geht weiter."
            : "The gate remains open – your soul journey continues."}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          data-testid="download-btn"
          onClick={onDownload}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6"
        >
          <Download className="w-4 h-4 mr-2" />
          {t.download_btn}
        </Button>
        <Button
          data-testid="new-reading-btn"
          variant="outline"
          onClick={onNew}
          className="border-primary/50 text-foreground hover:bg-secondary rounded-full px-8 py-6 bg-transparent"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {t.new_btn}
        </Button>
      </div>
    </motion.div>
  );
};
